/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { CREATE_POLICY, UPDATE_POLICY } from "../graphql";
import { VApplication } from "../../../application";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ICreatePolicyProps {
  selectedDocs: any;
  beneficiaryFirstName?: string;
  beneficiaryLastName?: string;
  beneficiaryStreetAddress?: string;
  beneficiaryCity?: string;
  beneficiaryCountry?: string;
  beneficiaryTelephone?: string;
  beneficiaryEmail?: string;
  policyOption?: any;
  policyCoverage: string;
  policyPremium?: string;
  intendedPurpose?: Record<string, boolean>;
  termsData?: {
    politicallyExposed?: string;
    charged?: Record<string, boolean>;
  };
  country?: string;
  kycData?: any;
  kycSubmissionDetails?: any;
  proofIds?: string[];
}
export interface ICreatePolicyMethods extends IPluginMethodMap {
  getCreatePolicyProps(args: ICreatePolicyProps): Promise<any>;
  getUpdatePolicyProps(args:string):Promise<any>;
}

export class CreatePolicyService implements IAgentPlugin {
  readonly methods: ICreatePolicyMethods;

  constructor() {
    this.methods = {
      getCreatePolicyProps: this.getProps.bind(this),
      getUpdatePolicyProps:this.updatePolicyProps.bind(this)
    };
  }
    

  async getProps(args: ICreatePolicyProps) {
    console.log(`args in create policy ---->`, args);
  const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    console.log("selected Docs data : -->", args?.kycData)
    console.log("kycSubmissionDetails :", args?.kycSubmissionDetails )
    
  try {
    if (client) {
      const oldproofIds: string[] =
        args?.selectedDocs?.map((item: any) => String(item?._id)) || [];
      const kycProofIds: string[] = args?.kycData?.docIds;
      const hasProofIds = Array.isArray(args?.proofIds) && args.proofIds.length > 0;
 
      const res = await client.mutate({
        mutation: CREATE_POLICY,
        variables: {
          input: {
            benificiaryDetail: {
              firstName: args?.beneficiaryFirstName,
              lastName: args?.beneficiaryLastName,
              email: args?.beneficiaryEmail,
              mobileNo: args?.beneficiaryTelephone,
              address: {
                addressLine1: args?.beneficiaryStreetAddress,
                city: args?.beneficiaryCity,
                country: args?.beneficiaryCountry,
              },
            },
            countryName: args?.country,
            coverage: parseFloat(args?.policyCoverage),
            premium: args?.policyOption?.premium?.raw,
            productDefination: args?.policyOption?.id,
            useOfProceeds: this.getUseOfProceeds(args?.intendedPurpose),
            termsOfUse: this.getTermsOfUse(args?.termsData),
            proofIds: hasProofIds ? args.proofIds : (kycProofIds || oldproofIds)
             
          },
        },
        fetchPolicy: "no-cache",
      });

      if (res?.data?.purchasePolicy) {
        return {
          policyNumber: res?.data?.purchasePolicy?.policyNumber,
          email: res?.data?.purchasePolicy?.purchasedBy?.email,
          phoneNumber:
            res?.data?.purchasePolicy?.purchasedBy?.primaryContactNo,
          phoneVerified:
            res?.data?.purchasePolicy?.purchasedBy?.phoneVerified,
          premium: res?.data?.purchasePolicy?.premium,
          discount: res?.data?.purchasePolicy?.discountedPremium,
          proofIds: res?.data?.purchasePolicy?.proofIds,
        };
      }
    }
  } catch (err: any) {
    console.log(`Create Policy error`, err);
    const gqlMessage =
      err?.graphQLErrors?.[0]?.extensions?.display_message ||
      err?.graphQLErrors?.[0]?.message ||
      err?.networkError?.result?.errors?.[0]?.message ||
      err?.response?.data?.error?.display_message ||
      err?.message ||
      "An error occurred while creating the policy.";
    throw new Error(gqlMessage);
  }
}

  private getUseOfProceeds(intendedPurpose: Record<string, boolean> = {}) {
  return Object.entries(intendedPurpose).map(([key, selected]) => ({
    selected,
    value: key,
  }));
}

private getTermsOfUse(termsData: any = {}) {
  let result: { selected: boolean; value: string }[] = [];

  if (termsData?.politicallyExposed) {
    result.push({
      selected: termsData.politicallyExposed === "yes",
      value: "Politically Exposed",
    });
  }

  Object.entries(termsData?.charged || {}).forEach(([key, selected]) => {
    result.push({ selected: Boolean(selected), value: key });
  });

  return result;
}

  public async updatePolicyProps(policyId: string) {
  const graphqlModule = getGraphQLModule();
  const client = graphqlModule.getClient("graphql-gateway");
    const updates = {
    isRenewed: "true",
  };

  try {
    if (client) {
      const res = await client.mutate({
        mutation: UPDATE_POLICY,
        variables: {
          input: {
            policyId: policyId,
            updates: updates,
          },
        },
        fetchPolicy: "no-cache",
      });

      if (res?.data?.updatePolicy) {
        console.log("Policy updated successfully");
        return true;
      }
    }
  } catch (err: any) {
    console.error("Update policy error:", err?.response?.data);
    throw new Error(err?.response?.data?.error?.display_message || "Update failed");
  }

  return false;
}

}
